import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";

import type { ProvinceEntity } from "@av/apps/recruitment/ats-common/types";

import type { DefaultOptionType } from "antd/es/select";
import type { TFunction } from "i18next";

export type ProvinceFilters = {
  searchText?: string;
  provinceCodes?: string[];
};
export const useProvinces = (filters: ProvinceFilters) => {
  const { t } = useTranslation();

  const query = useQuery({
    queryKey: ["provinces"],
    queryFn: async (): Promise<ProvinceEntity[]> => {
      const provinces: ProvinceEntity[] = [
        {
          code: "CA-AB",
          name: "Alberta",
          shortName: "AB",
        },
        {
          code: "CA-BC",
          name: "British Columbia",
          shortName: "BC",
        },
        {
          code: "CA-MB",
          name: "Manitoba",
          shortName: "MB",
        },
        {
          code: "CA-NB",
          name: "New Brunswick",
          shortName: "NB",
        },
        {
          code: "CA-NL",
          name: "Newfoundland and Labrador",
          shortName: "NL",
        },
        {
          code: "CA-NS",
          name: "Nova Scotia",
          shortName: "NS",
        },
        {
          code: "CA-ON",
          name: "Ontario",
          shortName: "ON",
        },
        {
          code: "CA-PE",
          name: "Prince Edward Island",
          shortName: "PE",
        },
        {
          code: "CA-QC",
          name: "Quebec",
          shortName: "QC",
        },
        {
          code: "CA-SK",
          name: "Saskatchewan",
          shortName: "SK",
        },
        {
          code: "CA-NT",
          name: "Northwest Territories",
          shortName: "NT",
        },
        {
          code: "CA-YT",
          name: "Yukon",
          shortName: "YT",
        },
        {
          code: "CA-NU",
          name: "Nunavut",
          shortName: "NU",
        },
      ];

      return provinces;
    },
    staleTime: Infinity,
  });

  const { searchText = "" } = filters;
  const normalizedSearchText = searchText.trim().toLowerCase();
  const filteredData = query.data?.filter(
    (province) =>
      (normalizedSearchText === "" ||
        province.code.toLowerCase().includes(normalizedSearchText) ||
        province.name.toLowerCase().includes(normalizedSearchText) ||
        province.shortName?.toLowerCase().includes(normalizedSearchText)) &&
      (!filters.provinceCodes?.length || filters.provinceCodes.includes(province.code))
  );

  const dataDropDown: DefaultOptionType[] =
    filteredData?.map(({ code, name }) => ({
      label: resolveProvinceTranslations(t, code, name),
      value: code,
    })) ?? [];

  return { ...query, data: filteredData, dataDropDown };
};

export function resolveProvinceTranslations(
  t: TFunction,
  provinceCode: string,
  fallbackName?: string
) {
  const provinceMap: Record<string, string> = {
    "CA-AB": t("recruitment.common.provinceEnum_ca_ab"),
    "CA-BC": t("recruitment.common.provinceEnum_ca_bc"),
    "CA-MB": t("recruitment.common.provinceEnum_ca_mb"),
    "CA-NB": t("recruitment.common.provinceEnum_ca_nb"),
    "CA-NL": t("recruitment.common.provinceEnum_ca_nl"),
    "CA-NS": t("recruitment.common.provinceEnum_ca_ns"),
    "CA-ON": t("recruitment.common.provinceEnum_ca_on"),
    "CA-PE": t("recruitment.common.provinceEnum_ca_pe"),
    "CA-QC": t("recruitment.common.provinceEnum_ca_qc"),
    "CA-SK": t("recruitment.common.provinceEnum_ca_sk"),
    "CA-NT": t("recruitment.common.provinceEnum_ca_nt"),
    "CA-YT": t("recruitment.common.provinceEnum_ca_yt"),
    "CA-NU": t("recruitment.common.provinceEnum_ca_nu"),
  };

  return provinceMap[provinceCode] ?? fallbackName ?? "-";
}

export function resolveLocation(province: string = "") {
  const [country, subdivision] = province.split("-");

  return {
    country: country,
    subdivision: subdivision,
  };
}
